import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Bank, UploadSimple, CircleNotch, FilePdf, FileXls, ArrowsClockwise, Trash, MagicWand,
  CheckCircle, WarningCircle, Question, Copy, ArrowDown, ArrowUp,
} from '@phosphor-icons/react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (v ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');

const RESULT = {
  matched: { label: 'Matched', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300', icon: CheckCircle },
  amount_mismatch: { label: 'Amount mismatch', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', icon: WarningCircle },
  missing_voucher: { label: 'No voucher', cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', icon: Question },
  not_in_bank: { label: 'Not in bank', cls: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300', icon: Bank },
  possible_duplicate: { label: 'Possible duplicate', cls: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300', icon: Copy },
};

const isBankLike = (a) => a.account_type === 'asset' && (
  ['bank', 'cash'].includes((a.sub_type || a.account_subtype || '').toLowerCase())
  || a.is_bank_account || /bank|cash|current account|savings|wallet/i.test(a.name || '')
);

async function download(url, name) {
  try {
    const res = await axios.get(url, { responseType: 'blob' });
    const href = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = href; a.download = name; a.click();
    window.URL.revokeObjectURL(href);
  } catch (e) {
    toast.error('Download failed');
  }
}

function ResultBadge({ status, testid }) {
  const r = RESULT[status] || { label: status, cls: '' };
  const Icon = r.icon || Question;
  return (
    <Badge variant="outline" className={`gap-1 border-0 ${r.cls}`} data-testid={testid}>
      <Icon size={12} weight="bold" /> {r.label}
    </Badge>
  );
}

function UploadCard({ accounts, onUploaded }) {
  const [accountId, setAccountId] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inputKey, setInputKey] = useState(0);

  const submit = async () => {
    if (!accountId || !file) { toast.error('Pick the bank account and the PDF statement'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('bank_account_id', accountId);
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/bank-statements/upload`, fd);
      toast.success('Statement uploaded — reading transactions…');
      setFile(null);
      setInputKey(k => k + 1);
      onUploaded(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card p-5 space-y-4" data-testid="bank-upload-card">
      <div className="flex items-center gap-2 font-semibold"><UploadSimple size={18} /> Upload bank statement (PDF)</div>
      <div className="grid gap-4 md:grid-cols-[1fr_1fr_auto] items-end">
        <div className="space-y-1">
          <Label className="text-xs">Bank account in your books</Label>
          <Select value={accountId} onValueChange={setAccountId}>
            <SelectTrigger data-testid="bank-account-select"><SelectValue placeholder="Choose account" /></SelectTrigger>
            <SelectContent>
              {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Statement PDF (max 15 MB)</Label>
          <input
            key={inputKey}
            type="file" accept="application/pdf,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-2 file:text-sm"
            data-testid="bank-statement-file-input"
          />
        </div>
        <Button onClick={submit} disabled={busy} data-testid="bank-statement-upload-btn">
          {busy ? <CircleNotch size={16} className="mr-2 animate-spin" /> : <UploadSimple size={16} className="mr-2" />}
          Compare with vouchers
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Every bank line is matched to a receipt (money in) or payment (money out) voucher on this account: same amount,
        date within 3 days, reference or name as tie-breaker. Scanned PDFs are read by AI — please glance over the result.
      </p>
    </div>
  );
}

function StatementList({ items, selectedId, onSelect, onDelete }) {
  if (!items.length) return null;
  return (
    <div className="rounded-lg border bg-card divide-y" data-testid="bank-statement-list">
      {items.map(s => (
        <div
          key={s.id}
          className={`flex flex-wrap items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/40 ${selectedId === s.id ? 'bg-muted/60' : ''}`}
          onClick={() => onSelect(s.id)}
          data-testid={`bank-statement-row-${s.id}`}
        >
          <Bank size={18} className="text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{s.file_name}</div>
            <div className="text-xs text-muted-foreground">
              {s.bank_account_name} · {s.period_from || '…'} → {s.period_to || '…'}
            </div>
          </div>
          {s.status === 'processing' && <Badge variant="outline" className="gap-1"><CircleNotch size={12} className="animate-spin" /> Reading…</Badge>}
          {s.status === 'failed' && <Badge variant="destructive" title={s.error}>Failed</Badge>}
          {s.status === 'ready' && (
            <span className="text-xs text-muted-foreground">
              {s.counts?.matched || 0} matched ·{' '}
              <span className="text-red-600 font-semibold">
                {(s.counts?.missing_voucher || 0) + (s.counts?.amount_mismatch || 0) + (s.counts?.not_in_bank || 0) + (s.counts?.possible_duplicate || 0)} issues
              </span>
            </span>
          )}
          <Button
            size="sm" variant="ghost"
            onClick={(e) => { e.stopPropagation(); onDelete(s); }}
            data-testid={`bank-statement-delete-${s.id}`}
          >
            <Trash size={14} />
          </Button>
        </div>
      ))}
    </div>
  );
}

function Kpis({ stmt, filter, setFilter }) {
  const c = stmt.counts || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {Object.keys(RESULT).map(k => (
        <button
          key={k} type="button"
          onClick={() => setFilter(filter === k ? 'all' : k)}
          className={`rounded-lg border p-3 text-left transition-colors hover:bg-muted/40 ${filter === k ? 'ring-2 ring-primary' : ''}`}
          data-testid={`bank-kpi-${k}`}
        >
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{RESULT[k].label}</div>
          <div className="text-2xl font-mono font-semibold mt-1">{c[k] || 0}</div>
        </button>
      ))}
    </div>
  );
}

function BalanceCheck({ stmt }) {
  const bc = stmt.balance_check;
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm rounded-lg border bg-muted/20 px-4 py-3" data-testid="bank-balance-check">
      <span>Money in: <b className="font-mono text-emerald-700 dark:text-emerald-400">{fmt(stmt.total_in) || '0.00'}</b></span>
      <span>Money out: <b className="font-mono text-rose-700 dark:text-rose-400">{fmt(stmt.total_out) || '0.00'}</b></span>
      {bc && (
        <span>
          Statement arithmetic:{' '}
          {Math.abs(bc.difference) < 0.01
            ? <b className="text-emerald-700 dark:text-emerald-400">opening + in − out = closing ✓</b>
            : <b className="text-red-600">off by {fmt(bc.difference)} — some lines may not have been read</b>}
        </span>
      )}
    </div>
  );
}

function ResultTable({ stmt, filter, selected, toggle }) {
  const rows = useMemo(() => {
    const bank = (stmt.lines || []).map(l => ({ key: `l${l.idx}`, src: 'Bank', ...l, status: l.match?.status, m: l.match || {} }));
    const books = (stmt.unmatched_vouchers || []).map(v => ({
      key: `v${v.id}`, src: 'Books', date: v.date, description: v.party, reference: v.reference,
      status: v.status_flag, m: { voucher_number: v.number, voucher_amount: v.amount, note: v.note, voucher_status: v.status },
      voucherKind: v.kind,
    }));
    return [...bank, ...books]
      .filter(r => filter === 'all' || r.status === filter)
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [stmt, filter]);

  return (
    <div className="rounded-lg border overflow-x-auto">
      <table className="w-full text-sm" data-testid="bank-result-table">
        <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 w-8" />
            <th className="px-3 py-2 text-left">Source</th>
            <th className="px-3 py-2 text-left">Date</th>
            <th className="px-3 py-2 text-left">Description</th>
            <th className="px-3 py-2 text-right">Money in</th>
            <th className="px-3 py-2 text-right">Money out</th>
            <th className="px-3 py-2 text-left">Voucher</th>
            <th className="px-3 py-2 text-right">Voucher amt</th>
            <th className="px-3 py-2 text-left">Result</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.length === 0 && (
            <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">Nothing here.</td></tr>
          )}
          {rows.map(r => (
            <tr key={r.key} className="hover:bg-muted/30" data-testid={`bank-row-${r.key}`}>
              <td className="px-3 py-2">
                {r.src === 'Bank' && r.status === 'missing_voucher' && (
                  <input
                    type="checkbox" checked={selected.has(r.idx)} onChange={() => toggle(r.idx)}
                    data-testid={`bank-line-select-${r.idx}`}
                  />
                )}
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground">{r.src}</td>
              <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.date}</td>
              <td className="px-3 py-2 max-w-[360px]">
                <div className="truncate" title={r.description}>{r.description || '—'}</div>
                {r.reference && <div className="text-[11px] text-muted-foreground truncate">Ref {r.reference}</div>}
                {r.m.note && <div className="text-[11px] text-amber-700 dark:text-amber-400">{r.m.note}</div>}
              </td>
              <td className="px-3 py-2 text-right font-mono text-emerald-700 dark:text-emerald-400">
                {r.src === 'Bank' ? fmt(r.credit) : ''}
              </td>
              <td className="px-3 py-2 text-right font-mono text-rose-700 dark:text-rose-400">
                {r.src === 'Bank' ? fmt(r.debit) : ''}
              </td>
              <td className="px-3 py-2 font-mono text-xs">
                {r.m.voucher_number || '—'}
                {['draft', 'pending'].includes(r.m.voucher_status) && (
                  <span className="ml-1 text-[10px] uppercase text-muted-foreground">({r.m.voucher_status})</span>
                )}
              </td>
              <td className="px-3 py-2 text-right font-mono">{fmt(r.m.voucher_amount)}</td>
              <td className="px-3 py-2"><ResultBadge status={r.status} testid={`bank-result-${r.key}`} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DraftDialog({ open, onClose, accounts, stmt, selected, onDone }) {
  const [incomeId, setIncomeId] = useState('');
  const [expenseId, setExpenseId] = useState('');
  const [busy, setBusy] = useState(false);
  const missing = (stmt?.lines || []).filter(l => l.match?.status === 'missing_voucher');
  const targets = selected.size ? missing.filter(l => selected.has(l.idx)) : missing;
  const ins = targets.filter(l => l.credit > 0).length;
  const outs = targets.length - ins;

  const submit = async () => {
    if ((ins && !incomeId) || (outs && !expenseId)) { toast.error('Pick the income and expense accounts'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/bank-statements/${stmt.id}/create-drafts`, {
        line_indexes: selected.size ? [...selected] : null,
        income_account_id: incomeId || expenseId,
        expense_account_id: expenseId || incomeId,
      });
      toast.success(`${data.created.length} draft voucher(s) created — review and approve them in Vouchers`);
      if (data.errors.length) toast.error(`${data.errors.length} line(s) failed: ${data.errors[0].error}`);
      onDone(data.statement);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create drafts');
    } finally {
      setBusy(false);
    }
  };

  const AccountPick = ({ value, onChange, types, testid }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Choose account" /></SelectTrigger>
      <SelectContent>
        {accounts.filter(a => types.includes(a.account_type)).map(a => (
          <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bank-draft-dialog">
        <DialogHeader><DialogTitle>Create draft vouchers</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          {targets.length} bank line(s) with no voucher: {ins} money in → draft receipt vouchers, {outs} money out → draft payment vouchers.
          Drafts post nothing to your accounts until you approve them.
        </p>
        {ins > 0 && (
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><ArrowDown size={12} /> Income account for receipts</Label>
            <AccountPick value={incomeId} onChange={setIncomeId} types={['income', 'revenue', 'liability', 'equity']} testid="bank-draft-income-account" />
          </div>
        )}
        {outs > 0 && (
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><ArrowUp size={12} /> Expense account for payments</Label>
            <AccountPick value={expenseId} onChange={setExpenseId} types={['expense', 'liability', 'asset']} testid="bank-draft-expense-account" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !targets.length} data-testid="bank-draft-confirm-btn">
            {busy && <CircleNotch size={14} className="mr-2 animate-spin" />} Create {targets.length} draft(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BankReconciliationPage() {
  const [accounts, setAccounts] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [stmt, setStmt] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [draftOpen, setDraftOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/bank-statements`);
    setItems(data || []);
    return data || [];
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/accounts`).then(r => setAccounts(r.data || [])).catch(() => {});
    loadList().then(list => list[0] && setSelectedId(list[0].id)).catch(() => toast.error('Failed to load statements'));
  }, [loadList]);

  useEffect(() => {
    if (!selectedId) { setStmt(null); return undefined; }
    let stop = false;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/bank-statements/${selectedId}`);
        if (stop) return;
        setStmt(data);
        if (data.status === 'processing') timer = setTimeout(poll, 4000);
        else loadList();
      } catch (e) {
        toast.error('Failed to load statement');
      }
    };
    setSelected(new Set());
    setFilter('all');
    poll();
    return () => { stop = true; clearTimeout(timer); };
  }, [selectedId, loadList]);

  const rematch = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/bank-statements/${stmt.id}/rematch`);
      setStmt(data);
      loadList();
      toast.success('Compared again with the latest vouchers');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-check failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.file_name}? Vouchers are not affected.`)) return;
    try {
      await axios.delete(`${API_URL}/bank-statements/${s.id}`);
      if (s.id === selectedId) setSelectedId(null);
      loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove statement');
    }
  };

  const toggle = (idx) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(idx)) n.delete(idx); else n.add(idx);
    return n;
  });

  const ready = stmt?.status === 'ready';
  const missingCount = stmt?.counts?.missing_voucher || 0;

  return (
    <div className="space-y-6" data-testid="bank-reconciliation-page">
      <div>
        <h1 className="text-3xl font-heading font-semibold flex items-center gap-2"><Bank size={28} /> Bank Reconciliation</h1>
        <p className="text-sm text-muted-foreground mt-1">Upload a bank statement and see every difference between the bank and your vouchers.</p>
      </div>

      <UploadCard accounts={accounts.filter(isBankLike)} onUploaded={(id) => { setSelectedId(id); loadList(); }} />
      <StatementList items={items} selectedId={selectedId} onSelect={setSelectedId} onDelete={remove} />

      {stmt?.status === 'processing' && (
        <div className="rounded-lg border p-8 text-center text-muted-foreground" data-testid="bank-processing">
          <CircleNotch size={28} className="mx-auto mb-2 animate-spin" /> Reading the statement and matching vouchers…
          {Date.now() - new Date(stmt.created_at).getTime() > 60000 && (
            <div className="text-xs mt-2">Long statements can take a couple of minutes — you can leave this page and come back.</div>
          )}
        </div>
      )}
      {stmt?.status === 'failed' && (
        <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30 p-4 text-sm text-red-700" data-testid="bank-failed">
          Could not read this statement: {stmt.error}
        </div>
      )}

      {ready && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="mr-auto">
              <div className="font-semibold">{stmt.bank_name || stmt.bank_account_name} {stmt.account_number ? `· ${stmt.account_number}` : ''}</div>
              <div className="text-xs text-muted-foreground">{stmt.period_from} → {stmt.period_to} · {stmt.lines.length} bank lines</div>
            </div>
            <Button size="sm" variant="outline" onClick={rematch} disabled={busy} data-testid="bank-rematch-btn">
              <ArrowsClockwise size={14} className="mr-1" /> Re-check
            </Button>
            <Button size="sm" variant="outline" onClick={() => download(`${API_URL}/bank-statements/${stmt.id}/report.pdf`, `Bank_Reconciliation_${stmt.period_to}.pdf`)} data-testid="bank-report-pdf-btn">
              <FilePdf size={14} className="mr-1" /> PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => download(`${API_URL}/bank-statements/${stmt.id}/report.xlsx`, `Bank_Reconciliation_${stmt.period_to}.xlsx`)} data-testid="bank-report-excel-btn">
              <FileXls size={14} className="mr-1" /> Excel
            </Button>
            {missingCount > 0 && (
              <Button size="sm" onClick={() => setDraftOpen(true)} data-testid="bank-create-drafts-btn">
                <MagicWand size={14} className="mr-1" />
                {selected.size ? `Create drafts for ${selected.size} selected` : `Create drafts for all ${missingCount} missing`}
              </Button>
            )}
          </div>
          <Kpis stmt={stmt} filter={filter} setFilter={setFilter} />
          <BalanceCheck stmt={stmt} />
          <ResultTable stmt={stmt} filter={filter} selected={selected} toggle={toggle} />
        </div>
      )}

      <DraftDialog
        open={draftOpen} onClose={() => setDraftOpen(false)} accounts={accounts} stmt={stmt} selected={selected}
        onDone={(s) => { setStmt(s); setSelected(new Set()); setDraftOpen(false); loadList(); }}
      />
    </div>
  );
}
